import React from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ReactMarkdown from 'react-markdown';
import { AnalysisResponse, AnalysisType } from './constants';

interface AnalysisResultsProps {
  analysis: AnalysisResponse;
  selectedAnalysisType: AnalysisType | undefined;
  onDownload: () => void;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  analysis,
  selectedAnalysisType,
  onDownload,
}) => {
  return (
    <Card className="space-y-6">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
            {selectedAnalysisType && (
              <span className="text-lg">{selectedAnalysisType.icon}</span>
            )}
            <span>{selectedAnalysisType?.name} Results</span>
          </h2>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Generated: {new Date(analysis.generated_at).toLocaleString()}
            </div>
            <Button
              onClick={onDownload}
              className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            >
              📥 Download
            </Button>
          </div>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded mt-4">
          <strong>Data Summary:</strong> {analysis.data_summary}
        </div>
      </div>
      <div className="prose dark:prose-invert max-w-none">
        <ReactMarkdown
          components={{
            h1: ({children}) => <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{children}</h1>,
            h2: ({children}) => <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">{children}</h2>,
            h3: ({children}) => <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 mt-4">{children}</h3>,
            p: ({children}) => <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed">{children}</p>,
            ul: ({children}) => <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-3 space-y-1">{children}</ul>,
            ol: ({children}) => <ol className="list-decimal list-inside text-gray-700 dark:text-gray-300 mb-3 space-y-1">{children}</ol>,
            li: ({children}) => <li className="text-gray-700 dark:text-gray-300">{children}</li>,
            strong: ({children}) => <strong className="font-semibold text-gray-900 dark:text-gray-100">{children}</strong>,
            em: ({children}) => <em className="italic text-gray-700 dark:text-gray-300">{children}</em>,
            blockquote: ({children}) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-600 dark:text-gray-400 my-4">{children}</blockquote>,
          }}
        >
          {analysis.analysis}
        </ReactMarkdown>
      </div>
    </Card>
  );
};

export default AnalysisResults;
